import { runtimeConfig } from './runtimeConfig';
import { getConstants } from 'alphalend-sdk';
import { StrategyContext } from 'alphafi-sdk-js';
/**
 * Single source of truth for the Sui gRPC-web endpoints, per network.
 *
 * Resolution is deliberately behind getSuiGrpcUrl() rather than an exported map, so no
 * caller can read a baked-in endpoint and silently bypass the runtime override below.
 *
 * Consumed by utils/client.ts (which owns the cached SuiGrpcClient per network); dApp Kit
 * gets its client from there too. Keep OUR endpoints here and nowhere else — note that
 * bundled third-party SDKs (e.g. @naviprotocol/lending) resolve their own endpoints over
 * JSON-RPC and do not go through here; those call sites need explicit overrides.
 */
/**
 * Mainnet defaults to our production BlockPI endpoint (gRPC-web; authenticated by the
 * per-app key below, which BlockPI domain-whitelists to our origins).
 * testnet/devnet default to the official public fullnodes.
 *
 * Keyed by NetworkType, so adding a network to NETWORKS is a compile error until it has
 * an endpoint here.
 */
const DEFAULT_GRPC_URLS = {
    mainnet: 'https://sui.blockpi.network',
    testnet: 'https://fullnode.testnet.sui.io:443',
    devnet: 'https://fullnode.devnet.sui.io:443',
};
/**
 * The alphafi-fe BlockPI API key, sent as `x-token` gRPC metadata and embedded in the
 * GraphQL URL path. Shipping it in the bundle is intentional: the key is
 * domain-whitelisted on BlockPI's side, so it only works from our origins (the old
 * Triton endpoint relied on the same origin gating, minus the visible key — the
 * practical exposure is quota consumption, not data access). Rotation = change this
 * constant AND the matching KEY_LINE_PROD literal in scripts/switch-rpc.mjs, then
 * redeploy.
 */
const BLOCKPI_KEY = 'e9fba0f4d8454939d0f1822ce72ffac373321b4c';
/** Public fullnodes take no token, hence '' for testnet/devnet. */
const DEFAULT_GRPC_TOKENS = {
    mainnet: BLOCKPI_KEY,
    testnet: '',
    devnet: '',
};
/**
 * Sui GraphQL endpoint per network, consumed by the SDK constructors (AlphaFiSDK /
 * AlphalendClient take a graphqlUrl). BlockPI embeds the API key in the URL path.
 * undefined means "let the SDK use its public default endpoint".
 */
const DEFAULT_GRAPHQL_URLS = {
    mainnet: `https://sui.blockpi.network/v1/graphql/${BLOCKPI_KEY}`,
    testnet: undefined,
    devnet: undefined,
};
/**
 * One rule couples every resolver in this file (the runtime mirror of the
 * all-or-nothing swap scripts/switch-rpc.mjs enforces at build time): move the
 * gRPC host off the baked-in endpoint and everything drops to unkeyed
 * defaults unless the matching override supplies a replacement. The test is
 * host identity, not env-var presence — setting `MAINNET_GRPC_URL` to the
 * BlockPI URL itself (a roll-forward after a rollback, or a declarative
 * manifest listing every variable) keeps the baked-in key.
 */
function onBakedInHost(network) {
    // Plain equality suffices: getSuiGrpcUrl canonicalizes every spelling of the
    // baked-in origin to the baked-in URL before it reaches anyone.
    return getSuiGrpcUrl(network) === DEFAULT_GRPC_URLS[network];
}
/**
 * Origin-identity test: a trailing slash, an explicit `:443`, or a path suffix
 * all name the same origin. Used by getSuiGrpcUrl to CANONICALIZE any spelling
 * of the baked-in origin to the bare canonical host — BlockPI serves gRPC only
 * on the bare host (keyed paths answer "Apikey not found"), so the coerced URL
 * is the only form that works. Fails closed on a malformed first argument: `b`
 * is always our parseable canonical URL, so a non-parsing `a` cannot match it.
 * Same predicate as alphafi-admin's rpcEndpoints.ts.
 */
function sameOrigin(a, b) {
    try {
        return new URL(a).origin === new URL(b).origin;
    }
    catch (_a) {
        return false;
    }
}
/**
 * The GraphQL endpoint for `network`, or undefined to use the SDK's default.
 * `*_GRAPHQL_URL` runtime overrides mirror the gRPC ones: GraphQL is just as
 * load-bearing, so ops must be able to redirect it without a rebuild too.
 * Coupled to the gRPC host (see onBakedInHost): a gRPC failover away from the
 * baked-in endpoint also moves GraphQL off the keyed BlockPI URL — otherwise
 * the SDK reads (AlphaFiSDK + every AlphalendClient) would stay pinned to the
 * degraded provider and the failover would only move half the traffic.
 *
 * CSP caveat (applies to the gRPC overrides equally): the browser only allows
 * hosts in security-headers.conf's connect-src list (*.sui.io, *.rpcpool.com,
 * sui.blockpi.network, *.alphafi.xyz, ...). Overrides within those families
 * work at runtime; pointing at a brand-new provider host additionally needs a
 * connect-src entry, which is a rebuild.
 */
export function getSuiGraphqlUrl(network) {
    const override = (runtimeConfig.getGraphqlUrl(network) || '').trim();
    if (override)
        return override;
    return onBakedInHost(network) ? DEFAULT_GRAPHQL_URLS[network] : undefined;
}
/**
 * The gRPC auth token for `network` ('' when the endpoint takes none). The
 * baked-in key belongs to the baked-in host and is sent only there (see
 * onBakedInHost) — never leaked to another host, but still sent when the URL
 * override deliberately points at the baked-in endpoint itself.
 */
export function getSuiGrpcToken(network) {
    const override = (runtimeConfig.getGrpcToken(network) || '').trim();
    if (override)
        return override;
    return onBakedInHost(network) ? DEFAULT_GRPC_TOKENS[network] : '';
}
/**
 * Points the AlphaLend SDK's Lazer price proxy at `LAZER_PROXY_URL` when that override is
 * set. Call once at startup, before any transaction is built.
 *
 * Mainnet only, and that is not the "only the startup network" mistake: testnet and devnet
 * resolve to the SDK's dev constants, whose Lazer proxy is already api-staging — the one
 * endpoint that allows a dev-server origin. Mainnet is the only network whose constants
 * name the blocked URL, so it is the only one that needs redirecting, switching networks
 * at runtime included.
 *
 * Borrow — and withdraw with debt, and the claim flows — needs an oracle price update,
 * which the SDK fetches from the LAZER_PROXY_URL in its own constants. The mainnet one,
 * api.alphalend.xyz, sends no Access-Control-Allow-Origin for http://localhost:3000, so a
 * dev server's request is blocked, the SDK retries three times and throws, and the
 * transaction is never built. Unlike the endpoints above, that URL is not a constructor
 * option, so this is the only way to redirect it from here.
 *
 * For local dev set it to https://api-staging.alphalend.xyz, which does allow the
 * localhost origin. Its blob carries the same Pyth Lazer signature, so it still verifies
 * against mainnet's Lazer state — confirmed by simulating the verify Move call
 * (pyth_lazer::parse_and_verify_le_ecdsa_update_v2) against the mainnet LAZER_STATE_ID
 * with the staging blob, using the prod blob as the control.
 *
 * Within this app's copy of the SDK, getConstants returns the one constants object every
 * AlphalendClient reads, so a single assignment covers all of them — that copy only, which
 * is half the bundle; see below. Left untouched when the override is empty, which is the
 * default and what production ships — `npm run rpc:local` sets the key, `npm run rpc:prod`
 * clears it.
 *
 * TWO copies of the AlphaLend SDK end up in the bundle, and both need redirecting. This app
 * resolves `alphalend-sdk` to the local submodule, while alphafi-sdk-js depends on the
 * published `@alphafi/alphalend-sdk` and carries its own nested copy: same package, same
 * version, separate module instances, so separate constants objects. Every vault strategy
 * that needs an oracle price update — slush lending and looping, looping, LYF,
 * single-asset looping, the ALPHA vault — runs on the nested one, which is why redirecting
 * only the first leaves those on the blocked URL.
 *
 * The nested copy is reached through StrategyContext, which alphafi-sdk-js exports and
 * which every strategy takes its client from. `alphalendClient.constants` is that copy's
 * shared constants object, so one throwaway context redirects all of them — checked by
 * mutating through one context and reading the value back from another.
 */
export function applyLazerProxyOverride() {
    const override = (runtimeConfig.getLazerProxyUrl() || '').trim();
    if (!override)
        return;
    const url = override.replace(/\/+$/, '');
    getConstants('mainnet').LAZER_PROXY_URL = url;
    try {
        new StrategyContext('mainnet').alphalendClient.constants.LAZER_PROXY_URL = url;
        // Read it back from a different context. That `constants` is the copy's shared module
        // object rather than a per-instance one is what makes a single assignment reach every
        // strategy, and nothing in the types holds that — a future SDK returning a per-instance
        // object would leave this silently doing nothing, surfacing as the same opaque CORS
        // failure it exists to remove.
        if (new StrategyContext('mainnet').alphalendClient.constants.LAZER_PROXY_URL !== url) {
            console.error("The vault SDK's Lazer proxy override did not stick — its constants are no longer shared.");
        }
    }
    catch (error) {
        // Reaching into another package's constants is a local-dev convenience, and this call
        // builds a whole client graph rather than reading a constant. It runs before the app
        // renders, so a throw here would cost the page rather than a price update.
        console.error("Could not redirect the vault SDK's Lazer proxy:", error);
    }
}
/**
 * gRPC endpoint + token as AlphalendClient's third (options) argument, so the
 * SDK's internal gRPC client rides the same endpoint/key as the app's own
 * client. The return type is derived from the SDK constructor rather than
 * restated, so a renamed option over there is a compile error here — the
 * object literal below gets excess-property checking against the real type.
 * (AlphaFiSDK's config is deliberately NOT built through this helper: its
 * inline literal in networkUtils.ts is excess-checked against its own type.)
 */
export function getSuiGrpcOptions(network) {
    return {
        grpcUrl: getSuiGrpcUrl(network),
        grpcToken: getSuiGrpcToken(network) || undefined,
    };
}
/**
 * The endpoint for `network`, preferring the runtime override over the baked-in default.
 *
 * `MAINNET_GRPC_URL` / `TESTNET_GRPC_URL` / `DEVNET_GRPC_URL` are injected at container start
 * (docker-entrypoint.sh -> public/runtime-config.js), so ops can redirect traffic away from
 * a degraded endpoint without a rebuild + redeploy. Empty (the default) means "use the
 * baked-in endpoint".
 *
 * The override must be a gRPC-web-capable endpoint: since the dApp Kit 2.0 migration the app
 * no longer speaks JSON-RPC, which is why these keys were renamed from `*_RPC_URL` — a value
 * left over from the JSON-RPC era is ignored rather than silently breaking every read. See
 * RENAMED_RPC_KEYS in runtimeConfig.ts.
 *
 * (No example URL in this comment on purpose: scripts/switch-rpc.mjs string-replaces
 * endpoint URLs in this file, and would rewrite the example along with the real one.)
 */
export function getSuiGrpcUrl(network) {
    // `|| ''` guards a runtime-config.js generated before these keys existed.
    const override = (runtimeConfig.getGrpcUrl(network) || '').trim();
    if (!override)
        return DEFAULT_GRPC_URLS[network];
    // Canonicalize any spelling of the baked-in origin (trailing slash, `:443`,
    // a pasted keyed path) to the baked-in URL ONCE, before the value reaches
    // any consumer — so the token decision and the URL the transports actually
    // post to can never disagree. (This gives up pointing at a path under the
    // baked-in host; today every such path is a form BlockPI rejects for gRPC.)
    return sameOrigin(override, DEFAULT_GRPC_URLS[network]) ? DEFAULT_GRPC_URLS[network] : override;
}
